import React, {
  Fragment,
  useEffect,
  useMemo,
  useCallback,
  useState,
} from 'react'
import EmptyMessage from 'components/App/DesktopView/Layout/RightSidebar/InAppCards/shared/EmptyMessage'
import CollapsableContent from 'components/CollapsableContent'
import { Button, Tab } from 'util/ui'
import { timeInHuman, strftime, toDate } from 'util/date'
import { titleize, humanize } from 'util/strings'
import SanitizedHTML from 'shared/components/ui/SanitizedHTML'
import NavigatableIntegrationWidgetCard from 'components/integrations/NavigatableIntegrationWidgetCard'
import { moneyString } from 'util/currency'
import ShopifyNewOrderModal from './ShopifyNewOrderModal'

const NoCustomer = () => {
  return <EmptyMessage>There is no customer information</EmptyMessage>
}

const RemainingFields = ({
  addresses,
  averageOrderAmount,
  customerId,
  lifetimeDuration,
  locale,
  lastOrderId,
  marketingOptInLevel,
  productSubscriberStatus,
  state,
  tags,
  taxExempt,
  taxExemptions,
  note,
  currency,
}) => {
  const averageOrderAmountString = moneyString({
    amount: averageOrderAmount,
    currencyCode: currency,
  })

  return (
    <Fragment>
      <p>Addresses: {addresses}</p>
      <p>Average Order Amount: {averageOrderAmountString}</p>
      <p>Customer ID: {customerId}</p>
      <p>Last order ID: {lastOrderId}</p>
      <p>Lifetime Duration: {lifetimeDuration}</p>
      <p>Locale: {locale}</p>
      <p>
        Marketing Opt In Level:
        {marketingOptInLevel && humanize(marketingOptInLevel)}
      </p>
      <p>Note: {note}</p>
      <p>Product Subscriber Status: {productSubscriberStatus}</p>
      <p>State: {state}</p>
      <p>Tags: {tags}</p>
      <p>Tax Exempt: {taxExempt}</p>
      <p>Tax Exemptions: {taxExemptions}</p>
    </Fragment>
  )
}

const TimelineEvent = ({ timelineEvent }) => {
  const {
    message,
    createdAt,
    attributeToApp,
    appTitle,
    __typename,
  } = timelineEvent
  const isComment = __typename === 'CommentEvent'
  const time = useMemo(
    () => {
      return strftime('%H:%M', createdAt)
    },
    [createdAt]
  )
  const author = attributeToApp ? appTitle : ''

  return (
    <div>
      {isComment && <div className="author">{author}</div>}
      <div className="message">
        <SanitizedHTML html={message} />
      </div>
      <div className="time">{time}</div>
    </div>
  )
}

const TimelineEventsForDay = ({ day, timelineEvents }) => {
  const dateString = useMemo(
    () => {
      return timeInHuman(new Date(day), { showToday: true })
    },
    [day]
  )

  if (!timelineEvents) return <Fragment />
  return (
    <div>
      <p>{dateString}</p>
      {timelineEvents.map(timelineEvent => (
        <TimelineEvent timelineEvent={timelineEvent} key={timelineEvent.id} />
      ))}
      <br />
    </div>
  )
}

const TimelineEvents = ({ timelineEventsByDay }) => {
  if (!timelineEventsByDay) return <Fragment />

  const eventGroups = []
  timelineEventsByDay.forEach((events, day) => {
    eventGroups.push(
      <TimelineEventsForDay
        day={day}
        key={events[0]?.id}
        timelineEvents={events}
      />
    )
  })

  return (
    <div>
      {eventGroups.slice(0, 4).map(eventGroup => eventGroup)}
      {eventGroups.length > 5 && (
        <CollapsableContent
          hideTrigger="– Hide events"
          showTrigger="+ Show more events"
        >
          <div className="ShopifyV2-events-more">
            {eventGroups.slice(4).map(eventGroup => eventGroup)}
          </div>
        </CollapsableContent>
      )}
    </div>
  )
}

const Order = ({ order, doNavigatableSetState, integration }) => {
  const onClick = useCallback(
    () => {
      doNavigatableSetState({ order, integration })
    },
    [doNavigatableSetState, order, integration]
  )

  return (
    <NavigatableIntegrationWidgetCard.ListItem
      onClick={onClick}
      subTitle={`${order.date} · ${titleize(order.displayFinancialStatus)}`}
      title={order.name}
    />
  )
}

const Orders = props => {
  const { orders } = props

  return (
    <>
      <p>Recent Orders</p>
      {orders.map(order => <Order order={order} key={order.id} {...props} />)}
    </>
  )
}

const NewOrderButton = () => {
  const [visible, setVisible] = useState(false)
  const handleClick = () => {
    setVisible(true)
  }
  const onClose = () => {
    setVisible(false)
  }

  return (
    <>
      <Button onClick={handleClick}>Create order</Button>
      <ShopifyNewOrderModal onClose={onClose} visible={visible} />
    </>
  )
}

const CustomerDetails = props => {
  const {
    contact,
    name,
    totalSpent,
    ordersCount,
    handle,
    integration,
    currency,
  } = props
  const customerUrl = `https://${
    integration.storeDomain
  }.myshopify.com/admin/customers/${handle}`
  const totalSpentString = moneyString({
    amount: totalSpent,
    currencyCode: currency,
  })
  const signedUpString = useMemo(
    () => {
      return toDate(contact.createdAt).toLocaleDateString('en-US')
    },
    [contact]
  )

  return (
    <Fragment>
      <p>Name: {name}</p>
      <p>Signed up: {signedUpString}</p>
      <p>Total spent: {totalSpentString}</p>
      <p>Total orders: {ordersCount}</p>
      <CollapsableContent
        hideTrigger="– Hide fields"
        showTrigger="+ Show all fields"
      >
        <div className="ShopifyV2-more">
          <RemainingFields {...props} />
        </div>
      </CollapsableContent>
      <Orders {...props} />
      <NewOrderButton />
      <a href={customerUrl} target="_blank" rel="noopener noreferrer">
        View in shopify
      </a>
    </Fragment>
  )
}

const DetailsContainer = props => {
  const { integration, handle, fetchEvents, fetchOrders } = props

  useEffect(
    () => {
      const gid = `gid://shopify/Customer/${handle}`
      fetchEvents(integration.id, gid, 15)
      fetchOrders(integration.id, gid, 3)
    },
    [integration.id, handle, fetchEvents, fetchOrders]
  )

  const panes = [
    { menuItem: 'Customer', render: () => <CustomerDetails {...props} /> },
    { menuItem: 'Timeline', render: () => <TimelineEvents {...props} /> },
  ]

  return <Tab panes={panes} secondary pointing />
}

export default function ShopifyIndex(props) {
  const { handle } = props
  return !handle ? <NoCustomer /> : <DetailsContainer {...props} />
}
